// Typed asset renderers (E / module 5 — the component-library seed).
//
// Generation wraps each deliverable's editable asset in <asset type="…" label="…">…</asset>.
// This module maps an asset `type` to a { Preview, Editor } pair so the app can render and edit
// each kind appropriately — today a clean default for any type, plus one differentiated example
// (email) to prove the pattern. Register richer components here as they're built (page, diagram,
// ad, sequence, …) and every surface that uses rendererFor() picks them up automatically.
//
// Why this exists: rendering an <asset> via rehype-raw left block markdown (## headings, lists)
// unparsed because CommonMark doesn't parse markdown inside a raw HTML block. Rendering the
// asset's content through its own Markdown pass fixes that AND gives us the typing hook.

import { useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import { agentsMarkdownComponents } from "./markdown";
import { splitSegments } from "./assetSegments";

export interface AssetPreviewProps { content: string; label?: string; type?: string; platform?: string }
export interface AssetEditorProps { content: string; onChange: (v: string) => void; label?: string; type?: string; platform?: string }

// fontClass lets a typed renderer give its asset a distinct typeface (e.g. the email body in
// serif) so the asset reads as a separate artifact from the surrounding sans-serif app chrome.
const md = (content: string, fontClass = "") => (
  <div className={`prose prose-sm max-w-none text-[13px] leading-relaxed text-gray-800 ${fontClass}`}>
    <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={agentsMarkdownComponents}>
      {content}
    </Markdown>
  </div>
);

// Small copy-to-clipboard control with a transient "Copied!" confirmation.
function CopyButton({ getText, title = "Copy" }: { getText: () => string; title?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); const t = getText(); if (!t) return; navigator.clipboard?.writeText(t).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {}); }}
      className="inline-flex items-center gap-1 rounded-md border border-black/[0.08] bg-white px-2 py-1 text-[11px] font-medium text-gray-600 shadow-sm transition-all duration-150 hover:bg-gray-50 hover:text-gray-900"
      title={title}
    >
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" /></svg>
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

// Header chip shown on every asset frame: the human label + the type, so it's clear which
// kind of asset (and renderer) this is.
function AssetFrameHeader({ label, type }: { label?: string; type?: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-black/[0.04] px-3 py-1.5">
      {label ? <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">{label}</span> : null}
      {type ? <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{type}</span> : null}
    </div>
  );
}

// ---- Default (any type): framed block + proper markdown render of the content ----
function DefaultAssetPreview({ content, label, type }: AssetPreviewProps) {
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-indigo-200/60 bg-white">
      <AssetFrameHeader label={label} type={type} />
      <div className="px-3 py-2">{md(content)}</div>
    </div>
  );
}
function DefaultAssetEditor({ content, onChange, label, type }: AssetEditorProps) {
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-indigo-200/60 bg-white">
      <AssetFrameHeader label={label} type={type} />
      <textarea
        value={content}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className="h-[44vh] w-full resize-y border-0 p-3 font-mono text-[12px] leading-relaxed text-gray-800 focus:outline-none"
      />
    </div>
  );
}

// ---- Email (differentiated example): pull the Subject onto a styled header row, render the
// rest as the email body. Demonstrates type-specific rendering; falls back to default shape. ----
function splitEmail(content: string): { subject: string | null; body: string } {
  const m = /^\s*\*\*subject:?\*\*\s*(.+)$/im.exec(content);
  if (!m) return { subject: null, body: content };
  // drop the matched subject line + a leading "## …Template" heading + a "**Body:**" label
  const body = content
    .replace(m[0], "")
    .replace(/^\s*##\s+.*$/m, "")
    .replace(/^\s*\*\*body:?\*\*\s*$/im, "")
    .trim();
  return { subject: m[1].trim(), body };
}
function EmailPreview({ content, label }: AssetPreviewProps) {
  const { subject, body } = splitEmail(content);
  const copyText = () => (subject ? `Subject: ${subject}\n\n` : "") + body;
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-indigo-200/60 bg-white">
      {/* header with the label/type chip + a copy-to-clipboard button (copies subject + body) */}
      <div className="flex items-center justify-between border-b border-black/[0.04] px-3 py-1.5">
        <span className="flex items-center gap-2">
          {label ? <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">{label}</span> : null}
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">email</span>
        </span>
        <CopyButton getText={copyText} title="Copy the email (subject + body)" />
      </div>
      <div className="border-b border-black/[0.06] bg-gray-50/70 px-3 py-2 text-[12px]">
        <div className="text-gray-400">Subject</div>
        <div className="font-serif font-semibold text-gray-800">{subject || <span className="font-sans font-normal italic text-gray-400">No subject line</span>}</div>
      </div>
      {/* the email body renders in serif so it reads as the email artifact, distinct from the app UI */}
      <div className="px-3 py-2">{md(body, "font-serif")}</div>
    </div>
  );
}

// ---- Email EDITOR (AMB-421, first rich typed editor): separate Subject + Body fields. ----
// Fully controlled: parse the asset content each render, rebuild on every keystroke, so the
// parent's single markdown string stays the source of truth (round-trips via parse∘build).
interface EmailParts { ok: boolean; heading?: string; subject: string; body: string }
function parseEmail(content: string): EmailParts {
  const heading = (/^##\s+.+$/m.exec(content) || [undefined])[0];
  const subj = /^\s*\*\*subject:?\*\*\s*(.+)$/im.exec(content);
  if (!subj) return { ok: false, subject: "", body: content };
  const bodyLabel = /^\s*\*\*body:?\*\*\s*$/im.exec(content);
  const after = bodyLabel ? content.slice(bodyLabel.index + bodyLabel[0].length) : content.slice(subj.index + subj[0].length);
  return { ok: true, heading, subject: subj[1].trim(), body: after.replace(/^\n+/, "") };
}
const buildEmail = (p: { heading?: string; subject: string; body: string }) =>
  [p.heading, `**Subject:** ${p.subject}`, "**Body:**", p.body].filter((x) => x != null && x !== "").join("\n\n");

function EmailEditor({ content, onChange, label }: AssetEditorProps) {
  const p = parseEmail(content);
  if (!p.ok) return <DefaultAssetEditor content={content} onChange={onChange} label={label} type="email" />;
  const set = (subject: string, body: string) => onChange(buildEmail({ heading: p.heading, subject, body }));
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-indigo-200/60 bg-white">
      <AssetFrameHeader label={label} type="email" />
      <div className="space-y-3 p-3">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-gray-400">Subject</span>
          <input
            value={p.subject}
            onChange={(e) => set(e.target.value, p.body)}
            className="w-full rounded-lg border border-black/[0.08] px-3 py-2 text-[13px] text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-gray-400">Body</span>
          <textarea
            value={p.body}
            onChange={(e) => set(p.subject, e.target.value)}
            spellCheck={false}
            className="h-[40vh] w-full resize-y rounded-lg border border-black/[0.08] p-3 font-mono text-[12px] leading-relaxed text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>
      </div>
    </div>
  );
}

// ---- CE-entry / spreadsheet PREVIEW (AMB-421): make it look + act like a sheet. ----
// Parse the first markdown table into a matrix so we can (a) render it as a clean grid with
// gridlines + zebra rows (sheet look) and (b) offer "Copy as TSV" → pastes straight into
// Google Sheets/Excel. A copy-paste-literal (code-fence) entry gets a prominent Copy instead.
function parseFirstTable(content: string): string[][] | null {
  const lines = content.split("\n");
  for (let i = 0; i < lines.length - 1; i++) {
    if (/^\s*\|.*\|\s*$/.test(lines[i]) && /^\s*\|?[\s:|-]*-{2,}[\s:|-]*\|?\s*$/.test(lines[i + 1])) {
      const rows: string[][] = [];
      const cells = (l: string) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      rows.push(cells(lines[i]));
      for (let j = i + 2; j < lines.length && /^\s*\|.*\|\s*$/.test(lines[j]); j++) rows.push(cells(lines[j]));
      return rows.length > 1 ? rows : null;
    }
  }
  return null;
}
const firstCodeFence = (content: string): string | null => {
  const m = /```[a-z]*\n([\s\S]*?)```/i.exec(content);
  return m ? m[1].replace(/\n$/, "") : null;
};

function CeEntryPreview({ content, label }: AssetPreviewProps) {
  const table = parseFirstTable(content);
  const fence = firstCodeFence(content);
  const tsv = table ? table.map((r) => r.join("\t")).join("\n") : "";
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-emerald-200/70 bg-white">
      <div className="flex items-center justify-between border-b border-black/[0.04] bg-emerald-50/40 px-3 py-1.5">
        <span className="flex items-center gap-2">
          {label ? <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">{label}</span> : null}
          <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">spreadsheet</span>
        </span>
        {table ? <CopyButton getText={() => tsv} title="Copy as rows — pastes into Google Sheets / Excel" /> : fence ? <CopyButton getText={() => fence} title="Copy this entry verbatim" /> : null}
      </div>
      {table ? (
        // Sheet-style grid: shaded header, gridlines, zebra rows, monospace cells.
        <div className="max-w-full overflow-x-auto p-2">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr>{table[0].map((h, i) => (<th key={i} className="border border-gray-300 bg-gray-100 px-2 py-1 text-left font-semibold text-gray-700">{h}</th>))}</tr>
            </thead>
            <tbody>
              {table.slice(1).map((row, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-gray-50/60" : "bg-white"}>
                  {row.map((c, ci) => (<td key={ci} className="border border-gray-200 px-2 py-1 align-top font-mono text-[11px] text-gray-700">{c}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : fence ? (
        <div className="p-3">
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-[12px] leading-relaxed text-gray-800">{fence}</pre>
        </div>
      ) : (
        <div className="px-3 py-2">{md(content)}</div>
      )}
    </div>
  );
}

// ---- Field extraction shared by the ad + social previews ----
// Pulls labeled fields ("Headline: …", "**Primary text:** …", or "## Headline\n…") from the
// asset content so the native frames can place each piece where the real platform shows it.
function extractField(content: string, names: string[]): string {
  for (const n of names) {
    const inl = new RegExp(`(?:^|\\n)\\s*\\**\\s*${n}\\s*\\**\\s*:\\s*\\**\\s*(.+)`, "i").exec(content);
    if (inl && inl[1].trim()) return inl[1].replace(/\*\*/g, "").trim();
  }
  for (const n of names) {
    const blk = new RegExp(`(?:^|\\n)#{1,4}\\s*${n}\\s*\\n+\\s*([^\\n]+)`, "i").exec(content);
    if (blk && blk[1].trim()) return blk[1].replace(/\*\*/g, "").trim();
  }
  return "";
}
const cleanUrl = (u: string) => u.replace(/^https?:\/\//, "").replace(/\/$/, "");

// ---- AD PREVIEW (AMB-421): renders in the real ad-platform chrome (Google search / Meta feed). ----
function GoogleAdCard({ headline, desc, url }: { headline: string; desc: string; url: string }) {
  return (
    <div className="mx-auto max-w-xl rounded-lg border border-gray-200 bg-white p-4 font-sans shadow-sm">
      <div className="mb-0.5 flex items-center gap-1.5 text-[12px] text-gray-700">
        <span className="font-bold">Sponsored</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-[10px] font-bold text-gray-500 ring-1 ring-gray-200">{(url[0] || "A").toUpperCase()}</span>
        <div className="leading-tight">
          <div className="text-[13px] font-medium text-gray-800">{cleanUrl(url).split("/")[0]}</div>
          <div className="text-[12px] text-gray-500">{cleanUrl(url)}</div>
        </div>
      </div>
      <a className="mt-1 block text-[20px] leading-tight text-[#1a0dab] hover:underline">{headline}</a>
      <p className="mt-1 text-[14px] leading-snug text-[#4d5156]">{desc}</p>
    </div>
  );
}
function MetaAdCard({ page, primary, headline, desc, cta, url, image }: { page: string; primary: string; headline: string; desc: string; cta: string; url: string; image: string }) {
  return (
    <div className="mx-auto max-w-sm overflow-hidden rounded-lg border border-gray-200 bg-white font-sans shadow-sm">
      <div className="flex items-center gap-2 px-3 pt-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-[13px] font-bold text-white">{(page[0] || "A").toUpperCase()}</span>
        <div className="leading-tight">
          <div className="text-[13px] font-semibold text-gray-900">{page}</div>
          <div className="flex items-center gap-1 text-[11px] text-gray-500">Sponsored · <span aria-hidden>🌐</span></div>
        </div>
      </div>
      {primary && <p className="px-3 py-2 text-[13px] leading-snug text-gray-800">{primary}</p>}
      <div className="flex aspect-[1.91/1] items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-center text-[11px] text-gray-400">
        <span className="px-6">{image || "Ad image"}</span>
      </div>
      <div className="flex items-center justify-between gap-2 bg-gray-50 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-[10px] uppercase tracking-wide text-gray-400">{cleanUrl(url)}</div>
          <div className="truncate text-[13px] font-semibold text-gray-900">{headline}</div>
          {desc && <div className="truncate text-[11px] text-gray-500">{desc}</div>}
        </div>
        <button className="shrink-0 rounded-md bg-gray-200 px-3 py-1.5 text-[12px] font-semibold text-gray-800">{cta}</button>
      </div>
    </div>
  );
}
function AdPreview({ content, label, platform }: AssetPreviewProps) {
  const headline = extractField(content, ["headline", "title"]) || (/^#{1,3}\s*(.+)/m.exec(content)?.[1]?.replace(/\*\*/g, "").trim() ?? "Your headline");
  const primary = extractField(content, ["primary text", "body", "primary", "post text"]);
  const desc = extractField(content, ["description", "link description"]);
  const cta = extractField(content, ["cta", "call to action", "button"]) || "Learn More";
  const url = extractField(content, ["display url", "url", "link", "destination"]) || "yourbank.com";
  const image = extractField(content, ["image", "visual", "creative", "graphic"]);
  const page = extractField(content, ["page", "advertiser", "brand"]) || "Your Bank";
  const plat = (platform || (/\b(meta|facebook|instagram)\b/i.test(content) ? "meta" : "google")).toLowerCase();
  const isMeta = ["meta", "facebook", "instagram"].includes(plat);
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-indigo-200/60 bg-white">
      <div className="flex items-center justify-between border-b border-black/[0.04] px-3 py-1.5">
        <span className="flex items-center gap-2">
          {label ? <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">{label}</span> : null}
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{isMeta ? "Meta ad" : "Google ad"}</span>
        </span>
        <CopyButton getText={() => content} title="Copy the ad copy" />
      </div>
      <div className="bg-gray-100 p-5">
        {isMeta ? <MetaAdCard page={page} primary={primary} headline={headline} desc={desc} cta={cta} url={url} image={image} /> : <GoogleAdCard headline={headline} desc={desc} url={url} />}
      </div>
    </div>
  );
}

// ---- SOCIAL PREVIEW (AMB-421): native post chrome (Facebook / Instagram / LinkedIn). ----
function SocialPreview({ content, label, platform }: AssetPreviewProps) {
  const caption = extractField(content, ["caption", "post text", "body", "copy"]) || content.replace(/^#.*$/m, "").replace(/(^|\n)\s*\*\*[^\n]+\*\*\s*:/g, "").trim();
  const image = extractField(content, ["image", "visual", "graphic"]);
  const hashtags = (content.match(/#[A-Za-z0-9_]+/g) || []).slice(0, 8).join(" ");
  const handle = extractField(content, ["page", "account", "handle", "brand"]) || "yourbank";
  const plat = (platform || (/\binstagram\b/i.test(content) ? "instagram" : /\blinkedin\b/i.test(content) ? "linkedin" : "facebook")).toLowerCase();
  const Avatar = ({ sz = "h-9 w-9" }: { sz?: string }) => <span className={`flex ${sz} items-center justify-center rounded-full bg-emerald-600 text-[13px] font-bold text-white`}>{(handle[0] || "A").toUpperCase()}</span>;
  const ImageBox = ({ aspect }: { aspect: string }) => <div className={`flex ${aspect} items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-center text-[11px] text-gray-400`}><span className="px-6">{image || "Post image"}</span></div>;
  let card;
  if (plat === "instagram") {
    card = (
      <div className="mx-auto max-w-xs overflow-hidden rounded-lg border border-gray-200 bg-white font-sans">
        <div className="flex items-center gap-2 px-3 py-2"><Avatar sz="h-7 w-7" /><span className="text-[13px] font-semibold text-gray-900">{handle}</span></div>
        <ImageBox aspect="aspect-square" />
        <div className="flex gap-3 px-3 pt-2 text-gray-700"><span>♥</span><span>💬</span><span>➤</span></div>
        <p className="px-3 py-2 text-[13px] text-gray-800"><span className="font-semibold">{handle}</span> {caption} {hashtags && <span className="text-blue-900">{hashtags}</span>}</p>
      </div>
    );
  } else if (plat === "linkedin") {
    card = (
      <div className="mx-auto max-w-sm overflow-hidden rounded-lg border border-gray-200 bg-white font-sans shadow-sm">
        <div className="flex items-center gap-2 px-3 pt-3"><Avatar /><div className="leading-tight"><div className="text-[13px] font-semibold text-gray-900">{handle}</div><div className="text-[11px] text-gray-500">Promoted</div></div></div>
        <p className="whitespace-pre-line px-3 py-2 text-[13px] leading-snug text-gray-800">{caption}{hashtags && <span className="text-blue-700"> {hashtags}</span>}</p>
        <ImageBox aspect="aspect-[1.91/1]" />
        <div className="flex justify-around border-t border-gray-100 py-1.5 text-[12px] text-gray-500"><span>👍 Like</span><span>💬 Comment</span><span>🔁 Repost</span><span>➤ Send</span></div>
      </div>
    );
  } else {
    card = (
      <div className="mx-auto max-w-sm overflow-hidden rounded-lg border border-gray-200 bg-white font-sans shadow-sm">
        <div className="flex items-center gap-2 px-3 pt-3"><Avatar /><div className="leading-tight"><div className="text-[13px] font-semibold text-gray-900">{handle}</div><div className="flex items-center gap-1 text-[11px] text-gray-500">Sponsored · <span aria-hidden>🌐</span></div></div></div>
        <p className="whitespace-pre-line px-3 py-2 text-[13px] leading-snug text-gray-800">{caption}{hashtags && <span className="text-blue-700"> {hashtags}</span>}</p>
        <ImageBox aspect="aspect-[1.91/1]" />
        <div className="flex justify-around border-t border-gray-100 py-1.5 text-[12px] text-gray-500"><span>👍 Like</span><span>💬 Comment</span><span>↪ Share</span></div>
      </div>
    );
  }
  const platLabel = plat === "instagram" ? "Instagram" : plat === "linkedin" ? "LinkedIn" : "Facebook";
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-indigo-200/60 bg-white">
      <div className="flex items-center justify-between border-b border-black/[0.04] px-3 py-1.5">
        <span className="flex items-center gap-2">
          {label ? <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">{label}</span> : null}
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{platLabel} post</span>
        </span>
        <CopyButton getText={() => caption + (hashtags ? "\n\n" + hashtags : "")} title="Copy the post text" />
      </div>
      <div className="bg-gray-100 p-5">{card}</div>
    </div>
  );
}

interface RendererPair { Preview: (p: AssetPreviewProps) => JSX.Element; Editor: (p: AssetEditorProps) => JSX.Element }
const DEFAULT: RendererPair = { Preview: DefaultAssetPreview, Editor: DefaultAssetEditor };

// The registry. Add typed renderers here as they're built; everything else uses DEFAULT.
const REGISTRY: Record<string, RendererPair> = {
  email: { Preview: EmailPreview, Editor: EmailEditor },
  "ce-entry": { Preview: CeEntryPreview, Editor: DefaultAssetEditor },
  spreadsheet: { Preview: CeEntryPreview, Editor: DefaultAssetEditor },
  ad: { Preview: AdPreview, Editor: DefaultAssetEditor },
  social: { Preview: SocialPreview, Editor: DefaultAssetEditor },
};
export const rendererFor = (type?: string): RendererPair => (type && REGISTRY[type]) || DEFAULT;

/**
 * Render a full deliverable body: scaffolding (read-only context) as markdown, each asset
 * through its typed Preview, in document order. A tagless body renders as plain markdown.
 */
export function AssetDocument({ body }: { body: string }) {
  const segs = splitSegments(body || "");
  const hasAssets = segs.some((s) => s.kind === "asset");
  if (!hasAssets) return md(body || "");
  return (
    <div>
      {segs.map((s, i) =>
        s.kind === "scaffold" ? (
          <div key={i}>{md(s.text)}</div>
        ) : (
          (() => { const R = rendererFor(s.type).Preview; return <R key={i} content={s.content} label={s.label} type={s.type} platform={s.platform} />; })()
        )
      )}
    </div>
  );
}
